/* embed-context.js — single source of truth for ?embed=widget URL handling.
 *
 * When loaded with ?embed=widget in the URL:
 *   • Stamps <body data-embed="widget" data-tenant="..."> so the global
 *     `/app/frontend/src/styles/embed.css` stylesheet can hide chrome
 *     (TableHeader, TableFooter, Concierge® pills, surface menu) with
 *     zero edits to the existing 1300-line Table/Escape/Enjoy files.
 *   • Persists member identity to sessionStorage so subsequent brief
 *     submissions (basket / concierge-request) can pre-fill + tag the
 *     payload with `tenant`, `member_email`, `member_first`,
 *     `member_last`, `member_phone`, `member_card`.
 *
 * Contract URL pattern (from LesConcierges × Aurelion integration doc):
 *   https://lesconcierges.com/table?embed=widget&tenant=aurelion
 *       &member_email=…&member_first=…&member_last=…
 *       &member_phone=…&member_card=…
 *
 * Anonymous embed (no member identity from host page) is supported —
 * only `embed=widget` is required. The brief form will ask for identity
 * the regular way in that case.
 */
const EMBED_PARAM_KEYS = [
  "tenant",
  "member_email",
  "member_first",
  "member_last",
  "member_phone",
  "member_card",
];

const SS_KEY = "lc_embed_ctx_v1";

function readUrlParams() {
  try {
    const sp = new URLSearchParams(window.location.search);
    if (sp.get("embed") !== "widget") return null;
    const ctx = { embed: "widget" };
    for (const k of EMBED_PARAM_KEYS) {
      const v = sp.get(k);
      if (v) ctx[k] = v;
    }
    // Compact mode · LC × Aurelion request 27 Jun 2026 · skip the
    // full search/results experience and render a single-paragraph
    // brief form instead (cuts time-to-Zoho-ticket from ~30s to ~5s).
    // Opt-in per surface; full search stays the default.
    if (sp.get("compact") === "1") ctx.mode = "compact";
    return ctx;
  } catch {
    return null;
  }
}

function persistToSession(ctx) {
  try {
    if (ctx) {
      window.sessionStorage.setItem(SS_KEY, JSON.stringify(ctx));
    }
  } catch {
    /* sessionStorage unavailable — degrade silently */
  }
}

function readFromSession() {
  try {
    const raw = window.sessionStorage.getItem(SS_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

/** Stamps document.body so CSS chrome-hiding can take over. */
function applyBodyAttrs(ctx) {
  if (typeof document === "undefined" || !document.body) return;
  document.body.setAttribute("data-embed", ctx.embed || "widget");
  if (ctx.tenant) document.body.setAttribute("data-tenant", ctx.tenant);
  if (ctx.mode) document.body.setAttribute("data-embed-mode", ctx.mode);
}

/** Initialise once at app boot (called from App.js). */
export function initEmbedContext() {
  if (typeof window === "undefined") return null;
  // 1 · URL is the source of truth on first load (fresh navigation
  //     from the embedding iframe). Sticky session keeps the flag set
  //     during in-app navigation (e.g. /table → /table?q=Paris).
  let ctx = readUrlParams();
  if (ctx) {
    persistToSession(ctx);
  } else {
    ctx = readFromSession();
  }
  if (ctx) applyBodyAttrs(ctx);
  return ctx;
}

/** Read the current embed context (cheap, no React state). */
export function getEmbedContext() {
  if (typeof window === "undefined") return null;
  return readFromSession() || readUrlParams();
}

/** Boolean helper. */
export function isEmbedded() {
  const ctx = getEmbedContext();
  return Boolean(ctx && ctx.embed === "widget");
}

/** True when `?embed=widget&compact=1` was on the entry URL. */
export function isCompactEmbed() {
  const ctx = getEmbedContext();
  return Boolean(ctx && ctx.embed === "widget" && ctx.mode === "compact");
}

// ────────────────────────────────────────────────────────────────────────
// Fetch interceptor · sends embed context with EVERY backend request
// ────────────────────────────────────────────────────────────────────────
//
// Why this exists (bug found 27 Jun 2026 evening): the original design
// detected embed context server-side via the `Referer` header alone. That
// works on the iframe's first form submission (URL still carries
// `?embed=widget&tenant=aurelion&member_*`). But the moment the React
// router pushes a new URL (e.g. user clicks a venue in `/table` results,
// goes into BookingSheet, posts the brief), the path changes and the
// `Referer` no longer has the tenant query param. Result: dispatcher
// fails to detect Aurelion → ticket lands in OUR Zoho instead of theirs.
//
// Fix: the embed context is already in sessionStorage (sticky). Inject
// it into every relevant backend request as a base64-encoded JSON header
// (`X-LC-Embed-Context`) so the backend sees it regardless of URL state.
// The backend dispatcher reads this header first (cleanest), then falls
// back to the body, then the Referer.
//
// We only patch `window.fetch` (not XMLHttpRequest) because all our
// brief-submit paths use fetch. axios is also affected because it uses
// fetch under the hood in modern builds; if it ever drops back to XHR,
// the body+Referer fallbacks still cover us.

function _encodeContextHeader(ctx) {
  try {
    const out = {
      tenant: ctx.tenant || null,
      mode: ctx.mode || null,
      member: {},
    };
    const map = {
      member_email:  "email",
      member_first:  "first_name",
      member_last:   "last_name",
      member_phone:  "whatsapp",
      member_card:   "member_card",
    };
    for (const [src, dst] of Object.entries(map)) {
      if (ctx[src]) out.member[dst] = ctx[src];
    }
    // btoa works on Latin-1; member fields may contain non-Latin chars.
    // Use a UTF-8-safe encoding.
    const json = JSON.stringify(out);
    if (typeof window !== "undefined" && typeof window.btoa === "function") {
      return window.btoa(unescape(encodeURIComponent(json)));
    }
    return null;
  } catch {
    return null;
  }
}

function _isBackendApiUrl(url) {
  if (!url) return false;
  // Resolve relative URLs against current origin
  let abs;
  try {
    abs = new URL(url, window.location.href);
  } catch {
    return false;
  }
  const apiBase = process.env.REACT_APP_BACKEND_URL || "";
  if (apiBase) {
    try {
      const apiU = new URL(apiBase);
      if (abs.origin === apiU.origin && abs.pathname.startsWith("/api")) {
        return true;
      }
    } catch {
      /* fall through */
    }
  }
  // Same-origin /api/* fallback (preview env)
  if (abs.pathname.startsWith("/api")) return true;
  return false;
}

function _installFetchInterceptor() {
  if (typeof window === "undefined" || !window.fetch) return;
  if (window.__lc_embed_fetch_patched) return;
  window.__lc_embed_fetch_patched = true;

  const origFetch = window.fetch.bind(window);

  window.fetch = async function patchedFetch(input, init = {}) {
    try {
      const ctx = getEmbedContext();
      if (!ctx || ctx.embed !== "widget") {
        return origFetch(input, init);
      }
      const url = typeof input === "string" ? input : (input && input.url) || "";
      if (!_isBackendApiUrl(url)) {
        return origFetch(input, init);
      }
      const header = _encodeContextHeader(ctx);
      if (!header) return origFetch(input, init);

      const newInit = { ...init };
      const headers = new Headers(newInit.headers || (input.headers || {}));
      // Don't overwrite an explicit header set by caller
      if (!headers.has("X-LC-Embed-Context")) {
        headers.set("X-LC-Embed-Context", header);
      }
      newInit.headers = headers;
      return origFetch(input instanceof Request ? new Request(input, newInit) : input, newInit);
    } catch {
      return origFetch(input, init);
    }
  };
}

// Install on module load — runs once at app boot.
if (typeof window !== "undefined") {
  _installFetchInterceptor();
}
